import { ReactNode } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { Container } from '@/blocks/components/Container';
import { AccentFragment } from '@/blocks/components/HeadingAccentFragment';
import { LinkSignup } from '@/blocks/components/PlatformLink';
import { SimpleTable } from '@/blocks/components/SimpleTable';
import simpleTableStyles from '@/blocks/components/SimpleTable/SimpleTable.module.scss';
import { CenterHeading } from '@/blocks/components/layout';
import { FormatAmount } from '@/blocks/formatters/format-price';
import { siteVersion } from '@/global/public-settings';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';

import { getDepositsWithdrawalsData } from '@/app/[lang]/crypto-futures/data';

import { CFAdvantagesTradingTable } from '../CFAdvantagesTradingTable';
import { AdvantagesTabs } from '../CryptoFuturesFeesAdvantagesSection';
import styles from './CryptoFuturesFeesAdvantagesTabContent.module.scss';

export const CryptoFuturesFeesAdvantagesTabContent = ({ selectedTab }: { selectedTab: AdvantagesTabs }) => {
  const tr = useTranslations();
  const { minimumDeposit, withdrawalFee } = getDepositsWithdrawalsData();

  const tablesContent: {
    [Tab in Exclude<AdvantagesTabs, AdvantagesTabs.TRADING>]: {
      columns: { keyColumn: ReactNode; valueColumn: ReactNode }[];
      footnote?: ReactNode;
    };
  } = {
    [AdvantagesTabs.ACCOUNT]: {
      columns: [
        {
          keyColumn: (
            <div className={simpleTableStyles.tableRowKeyColumn}>
              <span className={classNames(simpleTableStyles.tableRowTitle, typography.h6)}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.account.keys.title.0')}
              </span>
              <span className={classNames(simpleTableStyles.tableRowSubtitle, typography.subheadingSM)}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.account.keys.subheading.0')}
              </span>
            </div>
          ),
          valueColumn: (
            <div className={simpleTableStyles.tableRowValueColumn}>
              <div className={simpleTableStyles.tableScopedValue}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.free')}
              </div>
            </div>
          ),
        },
        {
          keyColumn: (
            <div className={simpleTableStyles.tableRowKeyColumn}>
              <span className={classNames(simpleTableStyles.tableRowTitle, typography.h6)}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.account.keys.title.1')}
              </span>
              <span className={classNames(simpleTableStyles.tableRowSubtitle, typography.subheadingSM)}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.account.keys.subheading.1')}
              </span>
            </div>
          ),
          valueColumn: (
            <div className={simpleTableStyles.tableRowValueColumn}>
              <div className={simpleTableStyles.tableScopedValue}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.free')}
              </div>
            </div>
          ),
        },
        {
          keyColumn: (
            <div className={simpleTableStyles.tableRowKeyColumn}>
              <span className={classNames(simpleTableStyles.tableRowTitle, typography.h6)}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.account.keys.title.2')}
              </span>
              <span className={classNames(simpleTableStyles.tableRowSubtitle, typography.subheadingSM)}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.account.keys.subheading.2')}
              </span>
            </div>
          ),
          valueColumn: (
            <div className={simpleTableStyles.tableRowValueColumn}>
              <div className={simpleTableStyles.tableScopedValue}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.free')}
              </div>
            </div>
          ),
        },
      ],
    },
    [AdvantagesTabs.DEPOSITS_WITHDRAWALS]: {
      columns: [
        {
          keyColumn: (
            <div className={simpleTableStyles.tableRowKeyColumn}>
              <span className={classNames(simpleTableStyles.tableRowTitle, typography.h6)}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.depositsWithdrawals.keys.title.0')}
              </span>
              <span className={classNames(simpleTableStyles.tableRowSubtitle, typography.subheadingSM)}>
                {tr.rich(
                  'pages.cryptoFutures.fees.sections.advantages.depositsWithdrawals.keys.subheading.0',
                )}
              </span>
            </div>
          ),
          valueColumn: (
            <div className={simpleTableStyles.tableRowValueColumn}>
              <span className={classNames(simpleTableStyles.tableRowSubtitle, typography.subheadingSM)}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.fromValue', {
                  span: () => (
                    <AccentFragment className={styles.boldAccent}>
                      <FormatAmount value={minimumDeposit} />
                    </AccentFragment>
                  ),
                })}
              </span>
            </div>
          ),
        },
        {
          keyColumn: (
            <div className={simpleTableStyles.tableRowKeyColumn}>
              <span className={classNames(simpleTableStyles.tableRowTitle, typography.h6)}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.depositsWithdrawals.keys.title.1')}
              </span>
              <span className={classNames(simpleTableStyles.tableRowSubtitle, typography.subheadingSM)}>
                {tr.rich(
                  'pages.cryptoFutures.fees.sections.advantages.depositsWithdrawals.keys.subheading.1',
                )}
              </span>
            </div>
          ),
          valueColumn: (
            <div className={simpleTableStyles.tableRowValueColumn}>
              <div className={simpleTableStyles.tableScopedValue}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.free')}
              </div>
            </div>
          ),
        },
        {
          keyColumn: (
            <div className={simpleTableStyles.tableRowKeyColumn}>
              <span className={classNames(simpleTableStyles.tableRowTitle, typography.h6)}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.depositsWithdrawals.keys.title.3')}
              </span>
              <span className={classNames(simpleTableStyles.tableRowSubtitle, typography.subheadingSM)}>
                {siteVersion.isAfrica
                  ? tr.rich(
                      'pages.cryptoFutures.fees.sections.advantages.depositsWithdrawals.keys.subheading.4',
                    )
                  : tr.rich(
                      'pages.cryptoFutures.fees.sections.advantages.depositsWithdrawals.keys.subheading.3',
                    )}
              </span>
            </div>
          ),
          valueColumn: (
            <div className={simpleTableStyles.tableRowValueColumn}>
              <span className={typography.subheadingSM}>
                {tr.rich('pages.cryptoFutures.fees.sections.advantages.startingFrom', {
                  span: () => (
                    <>
                      &#8212;&nbsp;
                      <AccentFragment className={styles.boldAccent}>
                        <FormatAmount value={withdrawalFee} />
                      </AccentFragment>
                    </>
                  ),
                })}
              </span>
            </div>
          ),
        },
      ],
    },
  };

  return (
    <div>
      <Container>
        <CenterHeading className={classNames(styles.heading)}>
          <h2 className={classNames(typography.h2)}>
            {tr.rich(`pages.cryptoFutures.fees.sections.advantages.${selectedTab}.title`)}
          </h2>
          <p className={classNames(typography.subheadingSM, typography.headerSubhead)}>
            {tr.rich(`pages.cryptoFutures.fees.sections.advantages.${selectedTab}.subheading`)}
          </p>
        </CenterHeading>
        {selectedTab === AdvantagesTabs.TRADING ? (
          <CFAdvantagesTradingTable />
        ) : (
          <SimpleTable content={tablesContent[selectedTab]} selectedTab={selectedTab} />
        )}
        <div className={styles.ctaWrapper}>
          <LinkSignup
            className={classNames(
              buttons.btn,
              buttons.btnM,
              buttons.btnResponsive,
              buttons.btnPrimary,
              styles.btnSpec,
            )}
            data-event-label={tr.rich('en.pages.cryptoFutures.fees.sections.advantages.cta')}
          >
            {tr.rich('pages.cryptoFutures.fees.sections.advantages.cta')}
          </LinkSignup>
        </div>
      </Container>
    </div>
  );
};
